import { Component } from "react"
import ErrorHandler from "src/components/errors/ErrorHandler"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import ApiHandler from "src/api/ApiHandler"
import StateConstants from "src/states/StateContstants"
import Loader from "src/components/general/Loading"
import AreaSearchForm from "src/components/search/AreaSearchForm"
import AreaAnalysisPageContent from "src/components/areas/AreaAnalysisPageContent"
import AreaNotFoundPage from "src/components/areas/AreaNotFoundPage"
import type { AnalysisBuilderChain } from "src/components/areas/areaAnalysisDescription/analysis_builders/AnalysisBuilderChain"
import "src/components/valuations/ValuationPage.css"
import posthog from "posthog-js"

interface AreaAnalysisPageProps {
    areaId: string
    stateHandler: EventAdaptingStateHandler
    apiHandler: ApiHandler
    errorHandler: ErrorHandler
    loader: Loader
    areaAnalysisBuilderChain: AnalysisBuilderChain
}

interface AreaAnalysisPageState {
    isLoading: boolean
    area: any
}

export default class AreaAnalysisPage extends Component<AreaAnalysisPageProps, AreaAnalysisPageState> {
    stateHandler: EventAdaptingStateHandler
    apiHandler: ApiHandler
    errorHandler: ErrorHandler
    loader: Loader
    hasLoaded: boolean = false
    lastAreaId: string | null = null

    constructor(props: AreaAnalysisPageProps) {
        super(props)
        this.stateHandler = props.stateHandler
        this.apiHandler = props.apiHandler
        this.errorHandler = props.errorHandler
        this.loader = props.loader
        this.state = {
            isLoading: true,
            area: null
        }
        this.stateHandler.addListener(StateConstants.IS_LOADING, (state: boolean) => {
            this.setState((prevState) => ({ ...prevState, isLoading: state }))
        })
        this.stateHandler.addListener(StateConstants.ERROR, () => {
            this.setState({})
        })
    }

    async componentDidMount() {
        await this.loadAreaIfNeeded()
    }

    async componentDidUpdate(prevProps: AreaAnalysisPageProps) {
        if (prevProps.areaId !== this.props.areaId) {
            this.hasLoaded = false
            this.lastAreaId = this.props.areaId
            this.stateHandler.set(StateConstants.AREA_STATE, null)
            this.setState({ isLoading: true, area: null })
            await this.loadAreaIfNeeded()
        }
    }

    async loadAreaIfNeeded() {
        if (!this.hasLoaded && this.props.areaId) {
            this.hasLoaded = true
            await this.loadArea()
            this.setState({ isLoading: false })
        }
    }

    async loadArea() {
        try {
            const area = await this.apiHandler.findArea({ id: this.props.areaId })
            this.stateHandler.set(StateConstants.AREA_STATE, area)
            this.setState({ area })
            posthog?.capture('area_analysis_viewed', { area_id: this.props.areaId, area_name: area?.name })
        } catch (error) {
            this.stateHandler.set(StateConstants.ERROR, error)
            this.setState({ area: null })
        }
    }

    render() {
        const { isLoading, area } = this.state
        const error = this.stateHandler.get("error")

        if (isLoading) {
            return this.loader.render("Loading area")
        }

        if (error) {
            if (ErrorHandler.isAreaNotFoundError(error)) {
                return <AreaNotFoundPage />
            }
            return this.errorHandler.render(error, {
                onDismiss: () => this.stateHandler.set(StateConstants.ERROR, null)
            })
        }

        const inlineError = this.stateHandler.get("error")
        return (
            <div>
                {inlineError && !ErrorHandler.isAreaNotFoundError(inlineError) && this.errorHandler.render(inlineError, {
                    onDismiss: () => this.stateHandler.set(StateConstants.ERROR, null)
                })}
                <AreaAnalysisPageContent
                    areaId={this.props.areaId}
                    area={area}
                    stateHandler={this.stateHandler}
                    apiHandler={this.apiHandler}
                    errorHandler={this.errorHandler}
                    loader={this.loader}
                    areaAnalysisBuilderChain={this.props.areaAnalysisBuilderChain}
                />
            </div>
        )
    }
}
